import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { getPerfumeShops, deletePerfumeShop } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Search, Gem, Trash2, Edit, Eye } from 'lucide-react';

export default function PerfumeShops() {
    const [shops, setShops] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [deleteId, setDeleteId] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    const fetchShops = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getPerfumeShops();
            setShops(res.data);
            setFiltered(res.data);
        } catch { toast.error('Failed to load shops'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchShops(); }, [fetchShops, location.key]);

    useEffect(() => {
        const q = search.toLowerCase();
        setFiltered(q ? shops.filter(s => s.name.toLowerCase().includes(q)) : shops);
    }, [search, shops]);

    const handleDelete = async () => {
        if (!deleteId) return;
        try {
            await deletePerfumeShop(deleteId);
            toast.success('Shop deleted');
            fetchShops();
        } catch { toast.error('Failed to delete'); }
        finally { setDeleteId(null); }
    };

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="perfume-shops-page">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-semibold text-[#2B2A27]">Perfume Shops</h1>
                        <p className="text-[#69665B] mt-1">{shops.length} shop{shops.length !== 1 ? 's' : ''}</p>
                    </div>
                    <Link to="/dashboard/perfume/shops/new">
                        <Button className="bg-[#D4AF37] hover:bg-[#C9A227] text-white rounded-full gap-2" data-testid="create-shop-btn">
                            <Plus className="w-4 h-4" /> New Shop
                        </Button>
                    </Link>
                </div>

                <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#969282]" />
                    <Input placeholder="Search shops..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 border-[#E5E1D1] rounded-xl" data-testid="search-shops" />
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{[1,2,3].map(i => <div key={i} className="h-40 bg-[#F0EDDF] rounded-2xl animate-pulse" />)}</div>
                ) : filtered.length === 0 ? (
                    <Card className="border-[#E5E1D1]">
                        <CardContent className="text-center py-16">
                            <Gem className="w-12 h-12 mx-auto mb-4 text-[#969282]" />
                            <p className="text-[#69665B] mb-4">{search ? 'No shops match your search' : 'No perfume shops yet'}</p>
                            {!search && <Link to="/dashboard/perfume/shops/new"><Button className="bg-[#D4AF37] hover:bg-[#C9A227] text-white rounded-full">Create your first shop</Button></Link>}
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filtered.map(shop => (
                            <Card key={shop.id} className="border-[#E5E1D1] overflow-hidden hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/dashboard/perfume/shops/${shop.id}`)} data-testid={`perfume-shop-card-${shop.id}`}>
                                <div className="h-3" style={{ backgroundColor: shop.primary_color || '#1A1A2E' }} />
                                <CardContent className="p-5">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: shop.primary_color || '#1A1A2E' }}>
                                                {shop.logo_url ? <img src={shop.logo_url} alt="" className="w-full h-full object-cover rounded-xl" /> : <Gem className="w-5 h-5 text-white" />}
                                            </div>
                                            <div>
                                                <p className="font-semibold text-[#2B2A27]">{shop.name}</p>
                                                <p className="text-xs text-[#969282]">/catalog/{shop.slug}</p>
                                            </div>
                                        </div>
                                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${shop.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{shop.status}</span>
                                    </div>
                                    {shop.description && <p className="text-sm text-[#69665B] mb-3 line-clamp-2">{shop.description}</p>}
                                    <div className="flex items-center justify-between pt-3 border-t border-[#E5E1D1]">
                                        <div className="flex items-center gap-1 text-xs text-[#69665B]"><Eye className="w-3.5 h-3.5" /> {shop.views || 0} views</div>
                                        <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/dashboard/perfume/shops/${shop.id}`)} data-testid={`edit-shop-${shop.id}`}><Edit className="w-3.5 h-3.5" /></Button>
                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => setDeleteId(shop.id)} data-testid={`delete-shop-${shop.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>

            <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete Perfume Shop?</AlertDialogTitle><AlertDialogDescription>This will permanently delete this shop and all its perfumes.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-600">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
            </AlertDialog>
        </DashboardLayout>
    );
}
